// Client lib imports
const airbrake = require('client/lib/airbrake')
const businessEvents = require('client/lib/business-events')
const { send: ajax } = require('client/lib/request')
// Common Client v2 imports
const { navigate } = require('client-v2/common/state/router/actions')
const { userProfileHref } = require('client-v2/common/models/user/actions')
const shareModalActions = require('client-v2/common/containers/share-modal/actions')
const toastsActions = require('client-v2/common/containers/toasts/actions')

// Action types

const COLLECTION_DELETED = 'COLLECTION_DELETED'
const COLLECTION_EDITED = 'COLLECTION_EDITED'
const COLLECTION_FOLLOWING_CHANGE = 'COLLECTION_FOLLOWING_CHANGE'

// Action enums

const COLLECTION_FOLLOWING_CHANGE_PENDING = 'COLLECTION_FOLLOWING_CHANGE_PENDING'

// Action creators

function collectionFollowingChange(collectionId, isFollowing) {
  return {
    type: COLLECTION_FOLLOWING_CHANGE,
    payload: {
      collectionId,
      isFollowing
    }
  }
}

function collectionFollowingChangePending(collectionId) {
  return collectionFollowingChange(
    collectionId,
    COLLECTION_FOLLOWING_CHANGE_PENDING
  )
}

function collectionFollowingToast(collection, isFollowing) {
  const message = isFollowing
  ? `You are now subscribed to "${collection.name}"`
  : `You have unsubscribed from "${collection.name}"`
  return toastsActions.showNotice({ message })
}

function collectionDeleted(collectionId) {
  return {
    type: COLLECTION_DELETED,
    payload: {
      collectionId
    }
  }
}

function collectionEdited(collection) {
  return {
    type: COLLECTION_EDITED,
    payload: {
      collectionId: collection.id,
      collection
    }
  }
}

// Action dispatchers

function navigateToCollectionHref(hrefFunc) {
  // Creates a navigation action dispatcher from a href helper function
  return function ({ event, pageContext, collection }) {
    return function (dispatch) {
      dispatch(navigate({
        event,
        pageContext,
        href: hrefFunc(collection)
      }))
    }
  }
}

function showCollectionEditModal({ collection }) {
  // Mitigate circular dependency
  const clientV1Bridge = require('client-v2/common/client-v1-bridge')
  return function () {
    clientV1Bridge.showMixEditModal({ mix: collection })
  }
}

function toggleFollowing({ collection, pageContext }) {
  // Mitigate circular dependency
  const clientV1Bridge = require('client-v2/common/client-v1-bridge')
  return async function (dispatch) {
    const mixId = collection.id
    const toggledFollowing = !collection.isFollowing
    const action = toggledFollowing ? 'follow' : 'unfollow'
    // Set front-end following state to "pending"
    dispatch(collectionFollowingChangePending(mixId))
    try {
      // Update following state on back-end
      await ajax({
        url: `/api/v0.1/mix/${mixId}/${action}`,
        method: 'post',
        data: { 'page_context': pageContext }
      })
    } catch (err) {
      airbrake.notify(err)
      // Revert the front-end following state
      dispatch(collectionFollowingChange(mixId, collection.isFollowing))
      // Inform the user that their action was unsuccessful
      clientV1Bridge.showAlertModal({
        heading: `Collection subscription could not be updated.`,
        subheading: `Something went wrong on our end. Please try again.`
      })
      return
    }
    // Update the front-end to reflect the successful action
    dispatch(collectionFollowingChange(mixId, toggledFollowing))
    dispatch(collectionFollowingToast(collection, toggledFollowing))
    // Send tracking call
    await businessEvents.trackSegment(`${action}-mix`, {
      'mix_id': mixId,
      slug: collection.slug
    }, {
      pageContext
    })
  }
}

function showCollectionShareModal({ collection }) {
  return shareModalActions.show({ model: collection })
}

// Helpers

function collectionProfileHref(collection) {
  return collectionProfilePostsHref(collection)
}

function collectionProfileEditHref(collection) {
  return `${collectionProfileHref(collection)}?edit=1`
}

function collectionProfileSubscribersHref(collection) {
  return `/${collection.username}/${collection.slug}/subscribers`
}

function collectionProfilePostsHref(collection) {
  return `/${collection.username}/${collection.slug}`
}

function collectionUserProfileHref(collection) {
  return userProfileHref(collection)
}

module.exports = {
  // Types
  COLLECTION_DELETED,
  COLLECTION_EDITED,
  COLLECTION_FOLLOWING_CHANGE,
  // Enums
  COLLECTION_FOLLOWING_CHANGE_PENDING,
  // Creators
  collectionDeleted,
  collectionEdited,
  collectionFollowingChange,
  collectionFollowingChangePending,
  // Dispatchers
  showCollectionEditModal,
  showCollectionShareModal,
  toggleFollowing,
  // Helpers
  collectionProfileHref,
  collectionProfileEditHref,
  collectionProfilePostsHref,
  collectionProfileSubscribersHref,
  collectionUserProfileHref
}

// Define navigation dispatchers
Object.entries({
  navigateToCollectionProfile: collectionProfileHref,
  navigateToCollectionProfileEdit: collectionProfileEditHref,
  navigateToCollectionProfilePosts: collectionProfilePostsHref,
  navigateToCollectionProfileSubscribers: collectionProfileSubscribersHref,
  navigateToCollectionUserProfile: collectionUserProfileHref
}).forEach(([ key, value ]) => {
  module.exports[key] = navigateToCollectionHref(value)
})
